import type { FunctionComponent } from 'react'

import {
	BaseButton,
	MOBILE_BREAKPOINT_DOWN,
	SPACING,
} from '@monthio/design-system'
import { useMemo } from 'react'
import { css, styled } from 'styled-components'

const BankSelectGrid = styled.ul`
	margin: 0;
	padding: 0;
	list-style: none;

	--grid-gap: ${SPACING.s};
	--grid-columns: 1fr 1fr;
	--cell-aspect-ratio: 3 / 1;
	width: 100%;

	display: grid;
	gap: var(--grid-gap);
	grid-template-columns: var(--grid-columns);

	> li {
		aspect-ratio: var(--cell-aspect-ratio);
	}

	@media screen and (min-width: ${MOBILE_BREAKPOINT_DOWN + 1}px) {
		--grid-gap: ${SPACING.m};
		--grid-columns: 1fr 1fr 1fr;
	}
`
const BankSelectGridItem = styled.li`
	aspect-ratio: var(--cell-aspect-ratio);

	& > * {
		height: 100%;
		width: 100%;
	}
`

export interface Bank {
	bannerSrc: string
	id: number
	name: string
}
export interface BankSelectProps {
	banks: Bank[]
	onSelect: (bankId: string) => Promise<void> | void
	isMobile?: boolean
}

export const BankSelect: FunctionComponent<BankSelectProps> = ({
	banks,
	onSelect,
	isMobile,
}) => {
	return (
		<BankSelectGrid>
			{banks.map(({ bannerSrc, id, name }) => (
				<BankSelectGridItem key={id}>
					<BankLogoButton
						bankName={name}
						bannerSrc={bannerSrc}
						isMobile={isMobile}
						// eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
						onSelect={() => onSelect(id.toString())}
					/>
				</BankSelectGridItem>
			))}
		</BankSelectGrid>
	)
}

const useBankSelectButtonCss = (imgSrc: string, isMobile?: boolean) => {
	return useMemo(
		() => css`
			background-color: #fff;
			background-image: url('${imgSrc}');
			background-size: contain;
			background-repeat: no-repeat;
			background-position: center;
			background-origin: content-box;

			&:not(:focus):not(:hover) {
				outline: 0;
			}
			&:hover {
				background-color: #fff;
			}
			padding: ${isMobile ? SPACING.xs : SPACING.s} ${SPACING.m};
		`,
		[imgSrc, isMobile],
	)
}

export interface BankLogoButtonProps {
	bankName: string
	bannerSrc: string
	isMobile?: boolean
	onSelect: () => Promise<void> | void
}

export const BankLogoButton: FunctionComponent<BankLogoButtonProps> = ({
	bankName,
	bannerSrc,
	isMobile,
	onSelect,
}) => {
	const backgroundImage = useBankSelectButtonCss(bannerSrc, isMobile)
	return (
		<BaseButton
			aria-label={bankName}
			buttonType="secondary"
			onClick={onSelect}
			overrides={backgroundImage}
			variant="neutral"
		/>
	)
}
